/**
 * SuspenseOutlet - Outlet wrapper with Suspense for lazy-loaded routes
 *
 * Wraps React Router's Outlet with Suspense boundary to handle lazy-loaded
 * page components. Used in layout components to provide consistent loading
 * states across all routes within that layout.
 *
 * Benefits:
 * - Single Suspense boundary per layout (not per route)
 * - Loading state appears inside layout shell (sidebar/header stay visible)
 * - Cleaner router configuration (no Suspense wrappers needed)
 * - Easy to customize loading per portal if needed
 */

import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

interface SuspenseOutletProps {
  /** Spinner size variant */
  size?: "sm" | "md" | "lg";
  /** Custom fallback component (overrides default spinner) */
  fallback?: React.ReactNode;
}

/**
 * Default loading fallback - centered spinner
 */
const DefaultFallback = ({ size = "lg" }: { size?: "sm" | "md" | "lg" }) => (
  <div className="flex items-center justify-center w-full h-full min-h-[400px]">
    <LoadingSpinner size={size} />
  </div>
);

export const SuspenseOutlet = ({ size = "lg", fallback }: SuspenseOutletProps) => {
  return (
    <Suspense fallback={fallback ?? <DefaultFallback size={size} />}>
      <Outlet />
    </Suspense>
  );
};
